import React, { useId, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import {
  format,
  parseISO,
  differenceInCalendarDays,
  differenceInDays,
  isAfter,
  isBefore,
  isValid,
  isWithinInterval,
  startOfDay,
  endOfDay,
  startOfYear,
  endOfYear,
  subDays,
  addDays,
} from 'date-fns';
import { useGetDepartmentsQuery } from '../store/slices/departmentSlice';
import type { Department } from '../store/slices/departmentSlice';
import { useGetEmployeesQuery } from '../store/slices/employeeSlice';
import type { Employee } from '../store/slices/employeeSlice';
import { useGetFilteredTimeOffEntriesQuery, TimeOffType } from '../store/slices/timeOffSlice';
import type { TimeOffEntry } from '../store/slices/timeOffSlice';
import { useAuth } from '../contexts/AuthContext';
import { focusRing, sbg } from '../theme/sbgTokens';
import { isManagerLike } from '../utils/roles';
import {
  formatCount,
  formatDate,
  formatDelta,
  formatDuration,
  formatNumber,
  formatPercent,
  formatTime,
  MISSING,
} from '../utils/format';
import { PageError } from '../components/common/PageState';
import {
  EmptyState,
  ErrorState,
  KpiGrid,
  KpiTile,
  LegendItem,
  Meter,
  PageHeader,
  PersonCell,
  SectionCard,
  StackedBar,
  StatusPill,
  useDelayedFlag,
} from '../components/ui';
import ManagerDashboard from './ManagerDashboard';

/* ------------------------------------------------------------------------ */
/* Shared shapes and helpers                                                 */
/* ------------------------------------------------------------------------ */

const INITIAL_SHOW = 5;
const PACE_DEPARTMENTS_SHOWN = 5;
const PACE_EMPLOYEES_SHOWN = 6;
const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;

/* Who's-out geometry (px). A column narrower than COLUMN_MIN truncates names. */
const ROW_HEIGHT = 56;
const COLUMN_MIN = 240;
const SUMMARY_WIDTH = 280;
const LIST_GAP = 24;
const PANEL_PADDING = 20;
/** Three full-width columns; below this the summary moves above the lists. */
const LISTS_MIN_WIDTH = COLUMN_MIN * 3 + LIST_GAP * 2 + PANEL_PADDING * 2;
/** Column header + five rows + "Show more", so paging days doesn't move the page below. */
const LISTS_MIN_HEIGHT = 36 + INITIAL_SHOW * ROW_HEIGHT + 36;

type RegionState = 'loading' | 'error' | 'ready';
type DayGroupKey = 'pto' | 'sick' | 'wfh';
type DayGroups = Record<DayGroupKey, TimeOffEntry[]>;

type PacedEmployee = Employee & {
  ptoUsed: number;
  sickUsed: number;
  totalUsed: number;
  totalAllotment: number;
  totalPace: number;
  isOverPace: boolean;
  departmentName: string;
};

type DepartmentPace = Department & {
  totalEmployees: number;
  overPacingEmployees: number;
  overPacingPercentage: number;
};

/**
 * Categories in the who's-out lists. Chart slots 1–3 in fixed order
 * (book §13.3); every swatch travels with its word.
 */
const DAY_GROUPS: { key: DayGroupKey; label: string; none: string; color: string }[] = [
  { key: 'pto', label: 'PTO', none: 'No one on PTO', color: sbg.chart.series[0] },
  { key: 'sick', label: 'Sick', none: 'No one out sick', color: sbg.chart.series[1] },
  { key: 'wfh', label: 'Working from home', none: 'No one working from home', color: sbg.chart.series[2] },
];
/** Out of office = PTO + Sick. Working from home is reported beside it, not inside it. */
const OUT_OF_OFFICE_GROUPS = DAY_GROUPS.filter((group) => group.key !== 'wfh');
const NONE_EXPANDED: Record<DayGroupKey, boolean> = { pto: false, sick: false, wfh: false };

/** Local calendar date as YYYY-MM-DD, so format.ts renders it without a timezone shift. */
const toYmd = (date: Date) => format(date, 'yyyy-MM-dd');

/** Hours keep one decimal when fractional (house pattern, as in TimeOffFormModal). */
const decimalsFor = (value: number) => (Number.isInteger(Math.round(value * 10) / 10) ? 0 : 1);
const formatHours = (value: number) => formatNumber(value, decimalsFor(value));

const regionState = (loading: boolean, failed: boolean): RegionState => (loading ? 'loading' : failed ? 'error' : 'ready');

/** "Employee and time-off data didn't load. Try again in a minute." — names only what failed. */
const failureBody = (sources: [failed: unknown, name: string][]) => {
  const names = sources.filter(([failed]) => Boolean(failed)).map(([, name]) => name);
  if (names.length === 0) return "The data didn't load. Try again in a minute.";
  const list = names.length === 1 ? names[0] : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${list.charAt(0).toUpperCase()}${list.slice(1)} data didn't load. Try again in a minute.`;
};

const rowLinkSx = {
  display: 'grid',
  gap: 0.75,
  py: 1.5,
  px: 1.25,
  mx: -1.25,
  borderRadius: sbg.radius.md,
  color: 'inherit',
  textDecoration: 'none',
  transition: `background-color ${sbg.duration.fast} ${sbg.ease.standard}`,
  '&:hover': { bgcolor: sbg.bg.hover },
  '&:focus-visible': focusRing,
} as const;

/* ------------------------------------------------------------------------ */
/* Who's out (the hero)                                                      */
/* ------------------------------------------------------------------------ */

const listsGridSx = {
  display: 'grid',
  gap: 3,
  alignContent: 'start',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${COLUMN_MIN}px), 1fr))`,
  minHeight: { md: LISTS_MIN_HEIGHT },
} as const;

/**
 * Summary beside the lists when the card is wide enough for three full
 * columns next to it; otherwise the summary becomes a row above them. The
 * 1px gap over a border-colored backdrop draws the divider in either case.
 */
const WhoIsOutLayout: React.FC<{ summary: React.ReactNode; lists: React.ReactNode }> = ({ summary, lists }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '1px', bgcolor: sbg.border.subtle }}>
    <Box sx={{ flex: `1 1 ${SUMMARY_WIDTH}px`, minWidth: 0, p: `${PANEL_PADDING}px`, bgcolor: sbg.bg.surface }}>
      {summary}
    </Box>
    <Box
      sx={{
        flex: '999 1 0%',
        minWidth: `min(100%, ${LISTS_MIN_WIDTH}px)`,
        p: `${PANEL_PADDING}px`,
        bgcolor: sbg.bg.surface,
      }}
    >
      {lists}
    </Box>
  </Box>
);

const PersonOutRow: React.FC<{ entry: TimeOffEntry; viewDate: Date }> = ({ entry, viewDate }) => {
  const team = entry.employee.teamName || entry.employee.department?.name || 'Unassigned';
  const start = parseISO(entry.startDate);
  const end = parseISO(entry.endDate);
  const multiDay = isValid(start) && isValid(end) && differenceInCalendarDays(end, start) > 0;
  const continues = isValid(end) && isAfter(startOfDay(end), startOfDay(viewDate));
  const secondary = continues ? `${team} · through ${formatDate(toYmd(end), { short: true })}` : team;
  const isPending = entry.status === 'PENDING';
  return (
    <Box
      component="li"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: ROW_HEIGHT,
        py: 0.75,
        borderTop: `1px solid ${sbg.border.subtle}`,
      }}
    >
      <Box title={`${entry.employee.name} · ${secondary}`} sx={{ flex: 1, minWidth: 0 }}>
        <PersonCell name={entry.employee.name} secondary={secondary} />
      </Box>
      <Box sx={{ display: 'grid', justifyItems: 'end', gap: 0.5, flexShrink: 0 }}>
        <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, whiteSpace: 'nowrap', ...TABULAR }}>
          {formatDuration(entry.hours * 60)}
          {/* A multi-day request's hours are its total, not hours out on this day. */}
          {multiDay && (
            <Box component="span" sx={{ color: sbg.text.secondary }}>
              {' total'}
            </Box>
          )}
        </Typography>
        {isPending && <StatusPill tone="warning">Pending</StatusPill>}
      </Box>
    </Box>
  );
};

const DayGroupColumn: React.FC<{
  label: string;
  none: string;
  color: string;
  entries: TimeOffEntry[];
  viewDate: Date;
  expanded: boolean;
  onToggle: () => void;
}> = ({ label, none, color, entries, viewDate, expanded, onToggle }) => {
  const moreId = useId();
  const shown = entries.slice(0, INITIAL_SHOW);
  const more = entries.slice(INITIAL_SHOW);
  return (
    <Box sx={{ minWidth: 0 }}>
      <Box component="h3" sx={{ m: 0, display: 'flex', alignItems: 'center', gap: 1, minHeight: 28, pb: 1 }}>
        <Box aria-hidden="true" sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: color, flex: 'none' }} />
        <Typography variant="overline" component="span" sx={{ color: sbg.text.secondary, minWidth: 0 }} noWrap>
          {label}
        </Typography>
      </Box>
      {entries.length === 0 ? (
        <Typography variant="body2" sx={{ color: sbg.text.secondary, py: 1.5, borderTop: `1px solid ${sbg.border.subtle}` }}>
          {none}
        </Typography>
      ) : (
        <>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {shown.map((entry) => (
              <PersonOutRow key={entry._id} entry={entry} viewDate={viewDate} />
            ))}
          </Box>
          {more.length > 0 && (
            <>
              <Collapse in={expanded} id={moreId}>
                <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {more.map((entry) => (
                    <PersonOutRow key={entry._id} entry={entry} viewDate={viewDate} />
                  ))}
                </Box>
              </Collapse>
              <Button
                size="small"
                variant="text"
                onClick={onToggle}
                aria-expanded={expanded}
                aria-controls={moreId}
                sx={{ mt: 0.5, ml: -1.25 }}
              >
                {expanded ? 'Show fewer' : `Show ${formatNumber(more.length)} more`}
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

const WhoIsOutSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label="Loading who's out" sx={{ visibility: visible ? 'visible' : 'hidden' }}>
      <WhoIsOutLayout
        summary={
          <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5 }}>
            <Skeleton variant="rounded" width={72} height={52} />
            <Skeleton variant="rounded" width={200} height={16} sx={{ maxWidth: '100%' }} />
            <Skeleton variant="rounded" height={8} sx={{ mt: 1 }} />
            {[0, 1, 2].map((n) => (
              <Skeleton key={n} variant="rounded" width={`${70 - n * 10}%`} height={14} />
            ))}
          </Box>
        }
        lists={
          <Box aria-hidden="true" sx={listsGridSx}>
            {[0, 1, 2].map((column) => (
              <Box key={column} sx={{ display: 'grid', gap: 1.5, alignContent: 'start' }}>
                <Skeleton variant="rounded" width="45%" height={14} />
                {[0, 1, 2].map((row) => (
                  <Box key={row} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Skeleton variant="circular" width={32} height={32} />
                    <Box sx={{ flex: 1, display: 'grid', gap: 0.75 }}>
                      <Skeleton variant="rounded" width={`${60 + ((row + column) % 3) * 10}%`} height={12} />
                      <Skeleton variant="rounded" width="40%" height={10} />
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        }
      />
    </Box>
  );
};

interface WhoIsOutCardProps {
  state: RegionState;
  groups: DayGroups;
  viewDate: Date;
  dayOffset: number;
  onDayOffsetChange: React.Dispatch<React.SetStateAction<number>>;
  canGoPrevDay: boolean;
  errorBody: string;
  onRetry: () => void;
}

const WhoIsOutCard: React.FC<WhoIsOutCardProps> = ({
  state,
  groups,
  viewDate,
  dayOffset,
  onDayOffsetChange,
  canGoPrevDay,
  errorBody,
  onRetry,
}) => {
  // "Show more" is per day: paging to another day starts collapsed.
  const [expandedState, setExpandedState] = useState({ day: dayOffset, groups: NONE_EXPANDED });
  const expanded = expandedState.day === dayOffset ? expandedState.groups : NONE_EXPANDED;
  const nextDayRef = useRef<HTMLButtonElement>(null);

  const relativeDay =
    dayOffset === 0 ? 'Today' :
    dayOffset === 1 ? 'Tomorrow' :
    dayOffset === -1 ? 'Yesterday' :
    null;
  const dateText = formatDate(toYmd(viewDate), { short: true, weekday: true });
  const dayPhrase = relativeDay ? relativeDay.toLowerCase() : `on ${dateText}`;

  const totalOut = groups.pto.length + groups.sick.length;
  const totalEntries = totalOut + groups.wfh.length;
  const outParts = OUT_OF_OFFICE_GROUPS.map((group) => ({
    key: group.key,
    label: group.label,
    value: groups[group.key].length,
    color: group.color,
  }));

  const goToToday = () => {
    onDayOffsetChange(0);
    // "Back to today" unmounts once we're on today; keep keyboard focus in the control group.
    nextDayRef.current?.focus();
  };

  const dayNav = (
    <Box role="group" aria-label="Choose a day" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {dayOffset !== 0 && (
        <Button size="small" variant="text" onClick={goToToday}>
          Back to today
        </Button>
      )}
      <Tooltip title={canGoPrevDay ? 'Previous day' : 'Earlier days are on the Time off page'}>
        {/* aria-disabled (not disabled) keeps it focusable, so the reason reaches keyboard and screen-reader users. */}
        <IconButton
          size="small"
          aria-label={canGoPrevDay ? 'Previous day' : 'Previous day (earlier days are on the Time off page)'}
          aria-disabled={canGoPrevDay ? undefined : true}
          onClick={canGoPrevDay ? () => onDayOffsetChange((offset) => offset - 1) : undefined}
          sx={{
            '&[aria-disabled="true"]': { color: sbg.text.disabled, cursor: 'default' },
            '&[aria-disabled="true"]:hover': { bgcolor: 'transparent' },
          }}
        >
          <ChevronLeft size={18} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Next day">
        <IconButton
          ref={nextDayRef}
          size="small"
          aria-label="Next day"
          onClick={() => onDayOffsetChange((offset) => offset + 1)}
        >
          <ChevronRight size={18} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  let body: React.ReactNode;
  if (state === 'loading') {
    body = <WhoIsOutSkeleton />;
  } else if (state === 'error') {
    body = <ErrorState compact title="Couldn't load who's out" body={errorBody} onRetry={onRetry} />;
  } else {
    const summary = (
      <Box
        sx={{
          display: 'grid',
          columnGap: 4,
          rowGap: 2,
          alignItems: 'start',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))',
        }}
      >
        <Box aria-live="polite" aria-atomic="true">
          <Typography variant="numericXl" component="p" sx={{ color: sbg.text.primary }}>
            {formatNumber(totalOut)}
          </Typography>
          <Typography variant="body1" component="p" sx={{ color: sbg.text.secondary }}>
            {totalOut === 1 ? 'person' : 'people'} out of office {dayPhrase}
          </Typography>
        </Box>
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          {totalOut > 0 && (
            <StackedBar
              height={8}
              parts={outParts}
              aria-label={`Out of office ${dayPhrase}: ${outParts.map((part) => `${part.label} ${part.value}`).join(', ')}`}
            />
          )}
          <Box component="ul" aria-label="Out of office by type" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1 }}>
            {outParts.map((part) => (
              <Box component="li" key={part.key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <LegendItem color={part.color} label={part.label} />
                <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, ...TABULAR }}>
                  {formatNumber(part.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            variant="body2"
            component="p"
            sx={{ color: sbg.text.secondary, pt: 1.5, borderTop: `1px solid ${sbg.border.subtle}`, ...TABULAR }}
          >
            {groups.wfh.length === 0
              ? 'No one working from home'
              : `${formatCount(groups.wfh.length, 'person', 'people')} working from home`}
          </Typography>
        </Box>
      </Box>
    );

    const lists =
      totalEntries === 0 ? (
        <Box sx={{ display: 'grid', alignContent: 'center', minHeight: { md: LISTS_MIN_HEIGHT } }}>
          <EmptyState
            compact
            title={`No one is out ${dayPhrase}`}
            body={`No PTO, sick time or work-from-home entries are on the calendar ${dayPhrase}.`}
          />
        </Box>
      ) : (
        <Box sx={listsGridSx}>
          {DAY_GROUPS.map((group) => (
            <DayGroupColumn
              key={group.key}
              label={group.label}
              none={group.none}
              color={group.color}
              entries={groups[group.key]}
              viewDate={viewDate}
              expanded={expanded[group.key]}
              onToggle={() => setExpandedState({ day: dayOffset, groups: { ...expanded, [group.key]: !expanded[group.key] } })}
            />
          ))}
        </Box>
      );

    body = <WhoIsOutLayout summary={summary} lists={lists} />;
  }

  return (
    <SectionCard
      id="dashboard-who-is-out"
      title="Who's out"
      description={relativeDay ? `${relativeDay} · ${dateText}` : dateText}
      action={state === 'ready' ? dayNav : undefined}
      flush
      footer={
        <Button
          component={RouterLink}
          to="/hr-time-off/calendar"
          size="small"
          variant="text"
          startIcon={<CalendarDays />}
          sx={{ ml: -1.25 }}
        >
          Open team calendar
        </Button>
      }
    >
      {body}
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Pace watch                                                                */
/* ------------------------------------------------------------------------ */

const summaryStripSx = {
  display: 'grid',
  gap: 3,
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
} as const;

const SummaryFigure: React.FC<{ label: string; value: React.ReactNode; caption: React.ReactNode }> = ({ label, value, caption }) => (
  <Box sx={{ display: 'grid', gap: 0.75, alignContent: 'start', minWidth: 0 }}>
    <Typography variant="overline" component="p" sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
    <Typography variant="numericMd" component="p" sx={{ color: sbg.text.primary }}>
      {value}
    </Typography>
    <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
      {caption}
    </Typography>
  </Box>
);

const PaceSkeleton: React.FC = () => {
  const visible = useDelayedFlag(true, 300);
  return (
    <Box role="status" aria-label="Loading pace watch" sx={{ visibility: visible ? 'visible' : 'hidden', p: 2.5, display: 'grid', gap: 3 }}>
      <Box aria-hidden="true" sx={summaryStripSx}>
        {[0, 1, 2].map((n) => (
          <Box key={n} sx={{ display: 'grid', gap: 1 }}>
            <Skeleton variant="rounded" width="50%" height={12} />
            <Skeleton variant="rounded" width={64} height={28} />
            <Skeleton variant="rounded" width="70%" height={12} />
          </Box>
        ))}
      </Box>
      <Box aria-hidden="true" sx={{ display: 'grid', gap: 1.5 }}>
        {[0, 1, 2, 3, 4].map((n) => (
          <Skeleton key={n} variant="rounded" height={14} width={`${90 - n * 8}%`} />
        ))}
      </Box>
    </Box>
  );
};

interface PaceWatchCardProps {
  state: RegionState;
  employees: PacedEmployee[];
  departments: DepartmentPace[];
  daysPassed: number;
  daysInYear: number;
  year: number;
  errorBody: string;
  onRetry: () => void;
}

const PaceWatchCard: React.FC<PaceWatchCardProps> = ({
  state,
  employees,
  departments,
  daysPassed,
  daysInYear,
  year,
  errorBody,
  onRetry,
}) => {
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [showAllEmployees, setShowAllEmployees] = useState(false);
  const employeesHeadingId = useId();

  // Display order only: matches the "Over by" column, furthest over first.
  const sortedEmployees = useMemo(
    () =>
      [...employees].sort(
        (a, b) => b.totalPace - b.totalAllotment - (a.totalPace - a.totalAllotment) || a.name.localeCompare(b.name)
      ),
    [employees]
  );
  const overAllotmentCount = useMemo(
    () => employees.filter((employee) => employee.totalUsed > employee.totalAllotment).length,
    [employees]
  );
  const visibleDepartments = showAllDepartments ? departments : departments.slice(0, PACE_DEPARTMENTS_SHOWN);
  const visibleEmployees = showAllEmployees ? sortedEmployees : sortedEmployees.slice(0, PACE_EMPLOYEES_SHOWN);

  let body: React.ReactNode;
  if (state === 'loading') {
    body = <PaceSkeleton />;
  } else if (state === 'error') {
    body = <ErrorState compact title="Couldn't load pace watch" body={errorBody} onRetry={onRetry} />;
  } else {
    body = (
      <>
        <Box sx={{ ...summaryStripSx, p: 2.5, borderBottom: `1px solid ${sbg.border.subtle}` }}>
          <SummaryFigure
            label="Employees over pace"
            value={formatNumber(employees.length)}
            caption="Projected past their yearly allotment"
          />
          <SummaryFigure
            label="Already over allotment"
            value={formatNumber(overAllotmentCount)}
            caption="Hours used exceed their yearly allotment"
          />
          <SummaryFigure
            label="Departments affected"
            value={formatNumber(departments.length)}
            caption="With at least one employee over pace"
          />
        </Box>

        {employees.length === 0 ? (
          <EmptyState
            compact
            title="No one is over pace"
            body={`At the current rate, every employee stays within their PTO and sick allotment for ${year}.`}
          />
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 5fr) minmax(0, 7fr)' } }}>
            <Box sx={{ p: 2.5, minWidth: 0, borderRight: { xl: `1px solid ${sbg.border.subtle}` } }}>
              <Typography variant="overline" component="h3" sx={{ color: sbg.text.secondary, m: 0 }}>
                Departments over pace
              </Typography>
              <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25, mb: 1 }}>
                Share of each department's employees over pace
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                {visibleDepartments.map((dept) => (
                  <Box component="li" key={dept._id} sx={{ '& + &': { borderTop: `1px solid ${sbg.border.subtle}` } }}>
                    <Box
                      component={RouterLink}
                      to={`/departments/${dept._id}`}
                      aria-label={`${dept.name}: ${formatNumber(dept.overPacingEmployees)} of ${formatCount(dept.totalEmployees, 'employee')} over pace (${formatPercent(dept.overPacingPercentage)})`}
                      sx={rowLinkSx}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, minWidth: 0 }}>
                        <Typography variant="labelMd" component="span" noWrap sx={{ color: sbg.text.primary, minWidth: 0 }}>
                          {dept.name}
                        </Typography>
                        <Typography variant="labelSm" component="span" sx={{ color: sbg.text.primary, flexShrink: 0, ...TABULAR }}>
                          {formatPercent(dept.overPacingPercentage)}
                        </Typography>
                      </Box>
                      <Box aria-hidden="true">
                        <Meter value={dept.overPacingPercentage} tone="warning" />
                      </Box>
                      <Typography variant="caption" component="span" sx={{ color: sbg.text.secondary, ...TABULAR }}>
                        {formatNumber(dept.overPacingEmployees)} of {formatCount(dept.totalEmployees, 'employee')} over pace
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
              {departments.length > PACE_DEPARTMENTS_SHOWN && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowAllDepartments((prev) => !prev)}
                  aria-expanded={showAllDepartments}
                  sx={{ mt: 1, ml: -1.25 }}
                >
                  {showAllDepartments ? 'Show fewer' : `Show all ${formatCount(departments.length, 'department')}`}
                </Button>
              )}
            </Box>

            <Box sx={{ minWidth: 0, borderTop: { xs: `1px solid ${sbg.border.subtle}`, xl: 0 } }}>
              <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                <Typography id={employeesHeadingId} variant="overline" component="h3" sx={{ color: sbg.text.secondary, m: 0 }}>
                  Employees over pace
                </Typography>
                <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
                  Approved PTO and sick hours, {year}
                </Typography>
              </Box>
              <Box
                role="region"
                aria-labelledby={employeesHeadingId}
                tabIndex={0}
                sx={{ overflowX: 'auto', '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: -2 } }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: 520,
                    // Employee stays in view while the numbers scroll on narrow screens.
                    '& .MuiTableCell-root:first-of-type': {
                      pl: 2.5,
                      position: 'sticky',
                      left: 0,
                      zIndex: 1,
                      bgcolor: sbg.bg.surface,
                    },
                    '& .MuiTableHead-root .MuiTableCell-root:first-of-type': { bgcolor: sbg.bg.surfaceSubtle },
                    '& .MuiTableBody-root .MuiTableRow-root:hover > .MuiTableCell-root:first-of-type': { bgcolor: sbg.bg.hover },
                    '& .MuiTableCell-root:last-of-type': { pr: 2.5 },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell align="right">Used (h)</TableCell>
                      <TableCell align="right">Projected (h)</TableCell>
                      <TableCell align="right">Allotment (h)</TableCell>
                      <TableCell align="right">Over by (h)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {visibleEmployees.map((employee) => {
                      const overAllotment = employee.totalUsed > employee.totalAllotment;
                      const overBy = employee.totalPace - employee.totalAllotment;
                      return (
                        <TableRow key={employee._id}>
                          <TableCell sx={{ maxWidth: 320 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <PersonCell
                                avatarName={employee.name}
                                name={
                                  <Link
                                    component={RouterLink}
                                    to={`/employees/${employee._id}`}
                                    underline="hover"
                                    sx={{ color: sbg.text.primary, '&:hover': { color: sbg.text.primary } }}
                                  >
                                    {employee.name}
                                  </Link>
                                }
                                secondary={employee.departmentName || 'No department'}
                              />
                              {overAllotment && <StatusPill tone="danger">Over allotment</StatusPill>}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{formatHours(employee.totalUsed)}</TableCell>
                          <TableCell align="right">{formatHours(employee.totalPace)}</TableCell>
                          <TableCell align="right">{formatHours(employee.totalAllotment)}</TableCell>
                          <TableCell align="right">{formatDelta(overBy, { unit: '', fractionDigits: decimalsFor(overBy) })}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
              {employees.length > PACE_EMPLOYEES_SHOWN && (
                <Box sx={{ px: 2.5, py: 1.5, borderTop: `1px solid ${sbg.border.subtle}` }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowAllEmployees((prev) => !prev)}
                    aria-expanded={showAllEmployees}
                    sx={{ ml: -1.25 }}
                  >
                    {showAllEmployees ? 'Show fewer' : `Show all ${formatCount(employees.length, 'employee')}`}
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </>
    );
  }

  return (
    <SectionCard
      id="dashboard-pace-watch"
      title="Pace watch"
      description={`Approved PTO and sick hours projected to Dec 31 at each person's current rate, against their yearly allotment. ${formatNumber(daysPassed)} of ${formatCount(daysInYear, 'day')} elapsed.`}
      action={
        state === 'ready' && employees.length > 0 ? (
          <Button component={RouterLink} to="/employees" size="small" variant="text" endIcon={<ArrowRight />}>
            View employees
          </Button>
        ) : undefined
      }
      flush
    >
      {body}
    </SectionCard>
  );
};

/* ------------------------------------------------------------------------ */
/* Admin / HR dashboard                                                      */
/* ------------------------------------------------------------------------ */

const AdminDashboard: React.FC = () => {
  const { data: departments, isLoading: isLoadingDepartments, error: departmentsError, refetch: reloadDepartments } = useGetDepartmentsQuery();
  const { data: employees, isLoading: isLoadingEmployees, error: employeesError, refetch: reloadEmployees } = useGetEmployeesQuery();
  // Fetch entries still active on/after Jan 1 (or 60 days back in early
  // January, so late-December pendings stay actionable). Bounding end_date
  // instead of start_date means a multi-month leave that began last year but
  // covers today still reaches the "out today" widget, while every entry the
  // YTD stats need (started this year) trivially satisfies end >= start >= Jan 1.
  const timeOffActiveAfter = useMemo(() => {
    const now = new Date();
    const lookback = subDays(now, 60);
    const jan1 = startOfYear(now);
    return format(lookback < jan1 ? lookback : jan1, 'yyyy-MM-dd');
  }, []);
  const {
    data: timeOffEntries,
    isLoading: isLoadingTimeOff,
    error: entriesError,
    refetch: reloadEntries,
    fulfilledTimeStamp: entriesUpdatedAt,
  } = useGetFilteredTimeOffEntriesQuery({
    endAfter: timeOffActiveAfter,
  });

  const today = useMemo(() => new Date(), []);
  // Day shown in the who's-out panel: 0 = today, 1 = tomorrow, -1 = yesterday.
  const [dayOffset, setDayOffset] = useState(0);
  const viewDate = useMemo(() => addDays(today, dayOffset), [today, dayOffset]);
  // Entries before this date were never fetched, so block navigating past it.
  const minNavDate = useMemo(() => parseISO(timeOffActiveAfter), [timeOffActiveAfter]);
  const canGoPrevDay = differenceInDays(startOfDay(viewDate), minNavDate) > 0;
  const currentYear = today.getFullYear();
  const yearStart = startOfYear(new Date(currentYear, 0, 1));
  const yearEnd = endOfYear(new Date(currentYear, 0, 1));
  const todayStart = startOfDay(today);
  const daysPassed = differenceInDays(today, yearStart);
  const daysInYear = differenceInDays(yearEnd, yearStart) + 1;
  const yearPercentComplete = Math.round((daysPassed / daysInYear) * 100);

  // Cross-reference time off entries with the active employee set so people
  // who have been terminated or resigned no longer surface in the "Today out"
  // panel or any other live dashboard count.
  const activeEmployeeIdSet = useMemo(
    () => new Set(
      (employees || [])
        .filter((e) => !e.employmentStatus || e.employmentStatus === 'active')
        .map((e) => e._id)
    ),
    [employees]
  );

  const outForDay = useMemo<DayGroups>(() => {
    if (!timeOffEntries) return { pto: [], sick: [], wfh: [] };
    const active = timeOffEntries.filter(e => {
      if (e.status === 'REJECTED' || e.hours === 0) return false;
      if (!activeEmployeeIdSet.has(e.employee?._id)) return false;
      try {
        return isWithinInterval(viewDate, { start: startOfDay(parseISO(e.startDate)), end: endOfDay(parseISO(e.endDate)) });
      } catch { return false; }
    });
    return {
      pto: active.filter(e => e.type === TimeOffType.PTO),
      sick: active.filter(e => e.type === TimeOffType.SICK),
      wfh: active.filter(e => e.type === TimeOffType.WFH),
    };
  }, [timeOffEntries, viewDate, activeEmployeeIdSet]);

  const pendingCount = useMemo(() =>
    (timeOffEntries || []).filter(e => e.status === 'PENDING' && e.hours > 0).length,
    [timeOffEntries]
  );

  const totalPtoHoursUsed = useMemo(() => {
    if (!timeOffEntries) return 0;
    return timeOffEntries
      .filter(e => e.type === TimeOffType.PTO && e.status === 'APPROVED' && isAfter(parseISO(e.startDate), yearStart) && isBefore(parseISO(e.startDate), yearEnd) && !isAfter(parseISO(e.startDate), todayStart))
      .reduce((sum, e) => sum + e.hours, 0);
  }, [timeOffEntries, yearStart, yearEnd, todayStart]);

  const employeePacing = useMemo<PacedEmployee[]>(() => {
    if (!employees || !timeOffEntries) return [];
    return employees.map((employee): PacedEmployee | null => {
      const empEntries = timeOffEntries.filter(e => e.employee._id === employee._id && isAfter(parseISO(e.startDate), yearStart) && isBefore(parseISO(e.startDate), yearEnd) && e.status === 'APPROVED' && !isAfter(parseISO(e.startDate), todayStart));
      const ptoUsed = empEntries.filter(e => e.type === TimeOffType.PTO).reduce((s, e) => s + e.hours, 0);
      const sickUsed = empEntries.filter(e => e.type === TimeOffType.SICK).reduce((s, e) => s + e.hours, 0);
      const totalUsed = ptoUsed + sickUsed;
      const totalAllotment = (employee.ptoAllotment || 0) + (employee.sickTimeAllotment || 0);
      const totalPace = daysPassed > 0 ? Math.round((totalUsed / daysPassed) * daysInYear) : 0;
      const isOverPace = totalAllotment > 0 && totalPace > totalAllotment;
      if (!isOverPace) return null;
      return { ...employee, ptoUsed, sickUsed, totalUsed, totalAllotment, totalPace, isOverPace, departmentName: departments?.find(d => d._id.toString() === employee.department?._id?.toString())?.name || '' };
    }).filter((employee): employee is PacedEmployee => employee !== null);
  }, [employees, timeOffEntries, departments, daysPassed, daysInYear, yearStart, yearEnd, todayStart]);

  const departmentPacing = useMemo<DepartmentPace[]>(() => {
    if (!departments || !employees || !employeePacing) return [];
    return departments.map(dept => {
      const deptId = dept._id.toString();
      const deptEmployees = employees.filter(e => e.department?._id?.toString() === deptId);
      const overPacing = employeePacing.filter(e => e && e.department?._id?.toString() === deptId);
      return { ...dept, totalEmployees: deptEmployees.length, overPacingEmployees: overPacing.length, overPacingPercentage: deptEmployees.length > 0 ? Math.round((overPacing.length / deptEmployees.length) * 100) : 0 };
    }).filter(d => d.overPacingEmployees > 0).sort((a, b) => b.overPacingPercentage - a.overPacingPercentage);
  }, [departments, employees, employeePacing]);

  const activeEmployees = employees?.filter(e => !e.employmentStatus || e.employmentStatus === 'active') || [];
  const totalEmployees = activeEmployees.length;
  const totalDepartments = departments?.length || 0;

  // Each region waits only for the data it reads, so a slow query never
  // blanks the whole page.
  const anyLoading = isLoadingDepartments || isLoadingEmployees || isLoadingTimeOff;
  const whoIsOutState = regionState(isLoadingEmployees || isLoadingTimeOff, Boolean(employeesError || entriesError));
  const paceState = regionState(anyLoading, Boolean(departmentsError || employeesError || entriesError));
  const entriesReady = !isLoadingTimeOff && !entriesError;

  const retryWhoIsOut = () => {
    if (employeesError) reloadEmployees();
    if (entriesError) reloadEntries();
  };
  const retryAll = () => {
    if (departmentsError) reloadDepartments();
    if (employeesError) reloadEmployees();
    if (entriesError) reloadEntries();
  };

  if (departmentsError && employeesError && entriesError) {
    return (
      <PageError
        title="Couldn't load the dashboard"
        description="Employee, department and time-off data didn't load. Try again in a minute."
        onRetry={retryAll}
      />
    );
  }

  const reviewLabel = entriesReady && pendingCount > 0 ? `Review ${formatCount(pendingCount, 'request')}` : 'Review requests';

  return (
    <Box>
      <PageHeader
        overline="Workspace"
        title="Dashboard"
        meta={[
          'All departments',
          formatDate(toYmd(today), { weekday: true }),
          `${formatPercent(yearPercentComplete)} through ${currentYear}`,
          entriesUpdatedAt ? `Updated ${formatTime(entriesUpdatedAt)}` : null,
        ]}
        actions={
          <>
            <Button component={RouterLink} to="/time-off" variant="outlined">
              View time off
            </Button>
            <Button component={RouterLink} to="/hr-time-off" variant="contained">
              {reviewLabel}
            </Button>
          </>
        }
      />

      <Box sx={{ display: 'grid', gap: 3 }}>
        <KpiGrid sx={{ mb: 0 }}>
          <KpiTile
            label="Employees"
            value={employeesError ? MISSING : formatNumber(totalEmployees)}
            caption={employeesError ? "Couldn't load" : 'Active headcount, as of today'}
            loading={isLoadingEmployees}
          />
          <KpiTile
            label="Departments"
            value={departmentsError ? MISSING : formatNumber(totalDepartments)}
            caption={departmentsError ? "Couldn't load" : 'Company-wide, as of today'}
            loading={isLoadingDepartments}
          />
          {/* A real link (new tab, middle-click, announced as a link). */}
          <KpiTile
            to="/hr-time-off"
            aria-label={
              entriesReady
                ? `Pending requests: ${formatNumber(pendingCount)}. Open the review queue`
                : 'Pending requests. Open the review queue'
            }
            label="Pending requests"
            value={entriesError ? MISSING : formatNumber(pendingCount)}
            caption={
              entriesError ? "Couldn't load" : (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {pendingCount > 0 ? 'Awaiting review' : 'Nothing waiting on review'}
                  <ArrowRight size={14} aria-hidden="true" />
                </Box>
              )
            }
            loading={isLoadingTimeOff}
          />
          <KpiTile
            label="PTO used"
            value={entriesError ? MISSING : `${formatHours(totalPtoHoursUsed)} ${totalPtoHoursUsed === 1 ? 'hour' : 'hours'}`}
            caption={entriesError ? "Couldn't load" : 'Approved, Jan 1 to today'}
            loading={isLoadingTimeOff}
          />
        </KpiGrid>

        <WhoIsOutCard
          state={whoIsOutState}
          groups={outForDay}
          viewDate={viewDate}
          dayOffset={dayOffset}
          onDayOffsetChange={setDayOffset}
          canGoPrevDay={canGoPrevDay}
          errorBody={failureBody([[employeesError, 'employee'], [entriesError, 'time-off']])}
          onRetry={retryWhoIsOut}
        />

        <PaceWatchCard
          state={paceState}
          employees={employeePacing}
          departments={departmentPacing}
          daysPassed={daysPassed}
          daysInYear={daysInYear}
          year={currentYear}
          errorBody={failureBody([[departmentsError, 'department'], [employeesError, 'employee'], [entriesError, 'time-off']])}
          onRetry={retryAll}
        />
      </Box>
    </Box>
  );
};

const Dashboard: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role;

  if (isManagerLike(role)) {
    return <ManagerDashboard />;
  }

  return <AdminDashboard />;
};

export default Dashboard;
